import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import * as moment from 'moment';
moment.locale('es');
const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';
const MIN_COLUMN_WIDTH = 12;
const MAX_COLUMN_WIDTH = 45;

@Injectable()
export class ExportExcelSiniestro {

  constructor() { }

  private normalizeValue(value: any): any {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    if (typeof value === 'string' && value.trim() === '') {
      return 'N/A';
    }

    if (typeof value === 'number' && Number.isNaN(value)) {
      return 'N/A';
    }

    return value;
  }

  private formatDate(value: any): string {
    if (!value) {
      return 'N/A';
    }

    const date = moment(value);
    return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : 'N/A';
  }

  private normalizeCuit(value: any): any {
    if (!value && value !== 0) {
      return 'N/A';
    }

    const cuit = String(value).replace(/\D/g, '');
    return cuit ? cuit : 'N/A';
  }

  private normalizeCoordinate(value: any): any {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const texto = String(value).trim();
    if (!texto) {
      return 'N/A';
    }

    const sanitized = texto.replace(/\s+/g, '').replace(/,/g, '.');
    const numeric = Number(sanitized);

    return Number.isFinite(numeric) ? numeric.toString() : 'N/A';
  }

  private normalizeEstado(value: any): string {
    const estado = String(value || '').trim();
    const map: { [key: string]: string } = {
      PENDIENTE: 'Pendiente',
      APROBADO: 'Aprobado',
      RECHAZADO: 'Rechazado',
      EN_CURSO: 'En Curso',
      PROCESO_INSPECCION: 'En Curso',
      ASEGURADO: 'Finalizado',
      CERRADO: 'Finalizado',
      FINALIZADO: 'Finalizado',
      ACTIVO: 'Activo',
      INACTIVO: 'Inactivo'
    };

    return map[estado] || (estado || 'N/A');
  }

  public async exportAsExcelFile(json: any[], excelFileName: string): Promise<void> {
    const data = await Promise.all(json.map(async (data) => {
      const latitud = data.lote?.establecimiento?.latitud;
      const longitud = data.lote?.establecimiento?.longitud;

      const hectareasResembradas = data.hasResembrada ?? data.hectareasResembradas ?? data.hectareasResembrada ?? 0;
      const cantidadInsumoResembrado = data.cantidadInsumoResembrado ?? data.bolsaResembrada ?? 0;

      return {
        'Id Siniestro': this.normalizeValue(data.id),
        'Id Plan de siembra': this.normalizeValue(data.loteId),
        'Empresa': this.normalizeValue(data.lote?.establecimiento?.user?.userPadre?.userPadre?.name),
        'CUIT Empresa': this.normalizeCuit(data.lote?.establecimiento?.user?.userPadre?.userPadre?.cuit),
        'Distribuidor': this.normalizeValue(data.lote?.establecimiento?.user?.userPadre?.name),
        'CUIT Distribuidor': this.normalizeCuit(data.lote?.establecimiento?.user?.userPadre?.cuit),
        'Productor': this.normalizeValue(data.lote?.establecimiento?.user?.name),
        'CUIT Productor': this.normalizeCuit(data.lote?.establecimiento?.user?.cuit),
        'Email Productor': this.normalizeValue(data.lote?.establecimiento?.user?.email),
        'Contacto': this.normalizeValue(data.lote?.establecimiento?.user?.phoneNumber),
        'Localidad': this.normalizeValue(data.lote?.establecimiento?.localidadCercana),
        'Provincia': this.normalizeValue(data.lote?.establecimiento?.provincia || data.provincia || 'N/A'),
        'Latitud': this.normalizeCoordinate(latitud),
        'Longitud': this.normalizeCoordinate(longitud),
        'Establecimiento': this.normalizeValue(data.lote?.establecimiento?.nombre),
        'Lote': this.normalizeValue(data.lote?.numeroLote),
        'Cultivo': this.normalizeValue(data.lote?.tipoCultivo),
        'Hectáreas': this.normalizeValue(data.lote?.hectareas),
        'Insumo': this.normalizeValue(data.lote?.insumo),
        'Cantidad Insumo': this.normalizeValue(data.lote?.cantidadInsumo),
        'Variedad': this.normalizeValue(data.lote?.variedad),
        'Fecha Siembra': this.normalizeValue(this.formatDate(data.lote?.fechaSiembra)),
        'Fecha Alta': this.normalizeValue(this.formatDate(data.lote?.createdAt)),
        'Contacto Siniestro': this.normalizeValue(data.numeroTelefono),
        'Hectáreas Afectadas': this.normalizeValue(data.superficieAfectada),
        'Hectáreas Resembradas': this.normalizeValue(hectareasResembradas),
        'Insumo Cubierto por Siniestro': this.normalizeValue(data.bolsaResembrada),
        'Excedente': this.normalizeValue(data.bolsaResembrada > data.lote?.cantidadInsumo ? data.bolsaResembrada - data.lote.cantidadInsumo : 0),
        'Cantidad Insumo Resembrado': this.normalizeValue(cantidadInsumoResembrado),
        'Evento': this.normalizeValue(data.evento),
        'Fecha Siniestro': this.normalizeValue(this.formatDate(data.fecha)),
        'Fecha Denuncia': this.normalizeValue(this.formatDate(data.createdAt)),
        'Estado Siniestro': this.normalizeEstado(data.estado),
        'Estado Plan': this.normalizeEstado(data.lote?.estado)
      };
    }));
    const myworksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    this.formatWorksheet(myworksheet);
    const myworkbook: XLSX.WorkBook = { Sheets: { 'data': myworksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(myworkbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  private formatWorksheet(worksheet: XLSX.WorkSheet): void {
    if (!worksheet['!ref']) {
      return;
    }

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '1F4E78' } },
      alignment: { horizontal: 'center', vertical: 'center' }
    };

    worksheet['!cols'] = [];

    for (let column = range.s.c; column <= range.e.c; column++) {
      let longestValue = 0;

      for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
        const cellValue = cell && cell.v !== undefined ? String(cell.v) : '';
        longestValue = Math.max(longestValue, cellValue.length);
      }

      const headerCell = worksheet[XLSX.utils.encode_cell({ r: range.s.r, c: column })] as any;
      if (headerCell) {
        headerCell.s = headerStyle;
      }

      worksheet['!cols'][column] = {
        wch: Math.min(Math.max(longestValue + 2, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH)
      };
    }

    worksheet['!rows'] = [{ hpt: 24 }];
  }

  private estadoToString(estado: string): string {
   switch (estado) {
      case 'PENDIENTE':
        return 'Pendiente';
      case 'PROCESO_INSPECCION':
        return 'En Curso';
      case 'EN_CURSO':
        return 'En Curso';
      case 'ASEGURADO':
        return 'Finalizado';
      case 'CERRADO':
        return 'Finalizado';
      case 'FINALIZADO':
        return 'Finalizado';
      case 'RECHAZADO':
        return 'Rechazado';
      default:
        return 'Desconocido';
    }
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_exported' + EXCEL_EXTENSION);
  }

}
